import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  hasAdminAccess,
  hasHubspotTicketAccess,
  hasPublisherStaffAccess,
  hasSuperadminAccess,
  isAuthenticated,
} from '../utils/roleUtils';
import { type AuthState, AuthStateType, useAuth } from './useAuth';

type RequireRole =
  | 'authenticated'
  | 'publisherStaff'
  | 'admin'
  | 'hubspotTickets'
  | 'superadmin';

interface UseRequireAuthOptions {
  requireRole?: RequireRole;
  redirectUnauthedTo?: string;
  redirectUnauthorizedTo?: string;
  replace?: boolean;
}

interface UseRequireAuthResult {
  authState: AuthState;
  isChecking: boolean;
  isAuthenticated: boolean;
  isAuthorized: boolean;
  canRender: boolean;
}

function isAuthorizedForRole(authState: AuthState, requireRole: RequireRole) {
  if (!isAuthenticated(authState)) {
    return false;
  }

  if (requireRole === 'authenticated') {
    return true;
  }

  if (requireRole === 'publisherStaff') {
    return hasPublisherStaffAccess(authState.user.role, authState.user.email);
  }

  if (requireRole === 'admin') {
    return hasAdminAccess(authState.user.role, authState.user.email);
  }

  if (requireRole === 'hubspotTickets') {
    return hasHubspotTicketAccess(authState.user.role, authState.user.email);
  }

  return hasSuperadminAccess(authState.user.role, authState.user.email);
}

export function useRequireAuth({
  requireRole = 'authenticated',
  redirectUnauthedTo = '/auth',
  redirectUnauthorizedTo = '/dashboard',
  replace = true,
}: UseRequireAuthOptions = {}): UseRequireAuthResult {
  const [authState] = useAuth();
  const navigate = useNavigate();
  const isChecking = authState.type === AuthStateType.Verifying;
  const authenticated = isAuthenticated(authState);
  const isAuthorized = isAuthorizedForRole(authState, requireRole);

  useEffect(() => {
    if (isChecking) {
      return;
    }

    if (authState.type === AuthStateType.NoToken) {
      navigate(redirectUnauthedTo, { replace });
      return;
    }

    if (!isAuthorized) {
      navigate(redirectUnauthorizedTo, { replace });
    }
  }, [
    authState.type,
    isAuthorized,
    isChecking,
    navigate,
    redirectUnauthedTo,
    redirectUnauthorizedTo,
    replace,
  ]);

  return {
    authState,
    isChecking,
    isAuthenticated: authenticated,
    isAuthorized,
    canRender: authenticated && isAuthorized && !isChecking,
  };
}
